import React from "react";
import { Head, Link, router } from "@inertiajs/react";
import { ExternalLink, Gavel, Loader2 } from "lucide-react";

import { PlatformConsoleBreadcrumb } from "@/components/platform_console/PlatformConsoleBreadcrumb";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { PaginatedTableControls } from "@/components/ui/paginated-table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { PaginationMeta } from "@/lib/jsonPagination";
import {
  isActiveAnalysis,
  mergeBillAnalysis,
  readWatchIds,
  shouldUnwatchAnalysis,
  statusVariant,
  watchIdsMissingFromPoll,
  writeWatchIds,
} from "@/lib/opsLegislationMonitorPolling";

const POLL_INTERVAL_MS = 4000;

type BillAnalysis = {
  status: string;
  updated_at: string | null;
  error_message: string | null;
  progress_label: string;
};

type BillRow = {
  id: number;
  name: string;
  state_abbreviation: string;
  document_type: string;
  madmin_url: string;
  analysis: BillAnalysis;
};

type BillStatusPollRow = BillAnalysis & { id: number };

type LegislationMonitorPageProps = {
  states: string[];
  selected_state: string | null;
  bills: BillRow[];
  pagination: PaginationMeta;
  notice?: string | null;
  alert?: string | null;
};

function formatTimestamp(value: string | null) {
  if (!value) return "Never";
  return new Date(value).toLocaleString();
}

function formatElapsed(iso: string | null, nowMs: number) {
  if (!iso) return null;
  const seconds = Math.max(0, Math.floor((nowMs - new Date(iso).getTime()) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${minutes}m ${remainder}s`;
}


function AnalysisStatusCell({
  analysis,
  billId,
  watchIds,
  nowMs,
}: {
  analysis: BillAnalysis;
  billId: number;
  watchIds: number[];
  nowMs: number;
}) {
  const active = isActiveAnalysis(analysis.status, watchIds.includes(billId), analysis.updated_at);
  const elapsed = (analysis.status === "processing" || active) ? formatElapsed(analysis.updated_at, nowMs) : null;

  if (active) {
    return (
      <div className="flex items-start gap-2">
        <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-muted-foreground" aria-hidden />
        <div className="space-y-1">
          <p className="font-medium leading-tight">{analysis.progress_label}</p>
          {elapsed ? <p className="text-xs text-muted-foreground">Elapsed {elapsed}</p> : null}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <Badge variant={statusVariant(analysis.status)}>{analysis.status}</Badge>
      {analysis.status !== "completed" ? (
        <p className="text-xs text-muted-foreground">{analysis.progress_label}</p>
      ) : null}
    </div>
  );
}

export default function LegislationMonitor({
  states,
  selected_state,
  bills,
  pagination,
  notice,
  alert,
}: LegislationMonitorPageProps) {
  const [processingBillId, setProcessingBillId] = React.useState<number | null>(null);
  const [watchIds, setWatchIds] = React.useState<number[]>(() => readWatchIds());
  const [analysisByBillId, setAnalysisByBillId] = React.useState<Record<number, BillAnalysis>>({});
  const [nowMs, setNowMs] = React.useState(() => Date.now());

  const addWatch = React.useCallback((billId: number) => {
    setWatchIds((previous) => {
      const next = [...new Set([...previous, billId])];
      writeWatchIds(next);
      return next;
    });
  }, []);

  const listParams = (overrides: { state?: string | null; page?: number; per_page?: number } = {}) => ({
    state: overrides.state ?? selected_state,
    page: overrides.page ?? pagination.current_page,
    per_page: overrides.per_page ?? pagination.per_page,
  });

  const billDetailHref = (billId: number) => {
    const params = new URLSearchParams();
    if (selected_state) params.set("state", selected_state);
    params.set("page", String(pagination.current_page));
    params.set("per_page", String(pagination.per_page));
    return `/ops/legislation-monitor/documents/${billId}?${params.toString()}`;
  };

  const mergedBills = React.useMemo(
    () =>
      bills.map((bill) => ({
        ...bill,
        analysis: mergeBillAnalysis(bill.analysis, analysisByBillId[bill.id]),
      })),
    [analysisByBillId, bills]
  );

  const hasActiveWork = React.useMemo(() => {
    const billIdsOnPage = new Set(mergedBills.map((bill) => bill.id));
    return (
      mergedBills.some((bill) =>
        isActiveAnalysis(bill.analysis.status, watchIds.includes(bill.id), bill.analysis.updated_at)
      ) || watchIds.some((id) => !billIdsOnPage.has(id))
    );
  }, [mergedBills, watchIds]);

  React.useEffect(() => {
    const processingIds = bills
      .filter((bill) => bill.analysis.status === "processing")
      .map((bill) => bill.id);
    if (processingIds.length === 0) return;

    setWatchIds((previous) => {
      const next = [...new Set([...previous, ...processingIds])];
      writeWatchIds(next);
      return next;
    });
  }, [bills]);

  React.useEffect(() => {
    setWatchIds((previous) => {
      const unwatchedIds = new Set(
        mergedBills
          .filter(
            (bill) =>
              previous.includes(bill.id) &&
              shouldUnwatchAnalysis(bill.analysis.status, true, bill.analysis.updated_at)
          )
          .map((bill) => bill.id)
      );
      if (unwatchedIds.size === 0) return previous;

      const next = previous.filter((id) => !unwatchedIds.has(id));
      writeWatchIds(next);
      return next;
    });
  }, [mergedBills]);

  React.useEffect(() => {
    if (!hasActiveWork) return;

    const tickId = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(tickId);
  }, [hasActiveWork]);

  React.useEffect(() => {
    if (!hasActiveWork) return;

    const poll = async () => {
      const ids = [...new Set([...watchIds, ...bills.map((bill) => bill.id)])];
      if (ids.length === 0) return;

      const params = new URLSearchParams();
      ids.forEach((id) => params.append("ids[]", String(id)));

      const response = await fetch(`/ops/legislation-monitor/bill_statuses?${params.toString()}`, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });
      if (!response.ok) return;

      const data = (await response.json()) as { bills: BillStatusPollRow[] };
      const nextAnalysis: Record<number, BillAnalysis> = {};
      const finishedIds = new Set<number>();
      const polledIds = new Set<number>();

      data.bills.forEach((row) => {
        const { id, ...analysis } = row;
        polledIds.add(id);
        nextAnalysis[id] = analysis;

        if (shouldUnwatchAnalysis(analysis.status, watchIds.includes(id), analysis.updated_at)) {
          finishedIds.add(id);
        }
      });

      watchIdsMissingFromPoll(watchIds, polledIds).forEach((id) => finishedIds.add(id));

      setAnalysisByBillId((previous) => ({ ...previous, ...nextAnalysis }));
      if (finishedIds.size > 0) {
        setWatchIds((previous) => {
          const next = previous.filter((id) => !finishedIds.has(id));
          writeWatchIds(next);
          return next;
        });
      }
    };

    void poll();
    const intervalId = window.setInterval(() => void poll(), POLL_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [bills, hasActiveWork, watchIds]);

  const onStateChange = (state: string) => {
    router.get("/ops/legislation-monitor", listParams({ state, page: 1 }), { preserveState: true, replace: true });
  };

  const onPageChange = (page: number) => {
    router.get("/ops/legislation-monitor", listParams({ page }), { preserveState: true, preserveScroll: true });
  };

  const onPerPageChange = (perPage: number) => {
    router.get("/ops/legislation-monitor", listParams({ page: 1, per_page: perPage }), {
      preserveState: true,
      preserveScroll: true,
    });
  };

  const onPrecompute = (billId: number) => {
    setProcessingBillId(billId);
    addWatch(billId);
    router.post(
      `/ops/legislation-monitor/documents/${billId}/precompute`,
      listParams(),
      {
        preserveScroll: true,
        onFinish: () => setProcessingBillId(null),
      }
    );
  };

  return (
    <>
      <Head title="Legislation Monitor" />

      <div className="container mx-auto max-w-6xl p-6">
        <div className="space-y-6">
          <PlatformConsoleBreadcrumb section="internal_tools" current="Legislation Monitor" />

          <div>
            <h1 className="flex items-center gap-2 text-3xl font-bold">
              <Gavel className="h-8 w-8" />
              Legislation Monitor
            </h1>
          </div>

          {alert ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{alert}</AlertDescription>
            </Alert>
          ) : null}

          {notice ? (
            <Alert>
              <AlertDescription>{notice}</AlertDescription>
            </Alert>
          ) : null}

          <Card>
            <CardHeader>
              <CardTitle>Bills by state</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="max-w-xs space-y-2">
                <Label htmlFor="state">State</Label>
                <Select value={selected_state ?? undefined} onValueChange={onStateChange} disabled={states.length === 0}>
                  <SelectTrigger id="state">
                    <SelectValue placeholder="Select a state" />
                  </SelectTrigger>
                  <SelectContent>
                    {states.map((state) => (
                      <SelectItem key={state} value={state}>
                        {state}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {states.length === 0 ? (
                <p className="text-sm text-muted-foreground">No potential state law documents found.</p>
              ) : pagination.total_count === 0 ? (
                <p className="text-sm text-muted-foreground">No bills for {selected_state}.</p>
              ) : (
                <>
                  <div className="overflow-x-auto rounded-md border">
                    <table className="min-w-full text-sm">
                      <thead className="bg-muted/50 text-left">
                        <tr>
                          <th className="px-4 py-3 font-medium">Bill</th>
                          <th className="px-4 py-3 font-medium">Analysis status</th>
                          <th className="px-4 py-3 font-medium">Last updated</th>
                          <th className="px-4 py-3 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mergedBills.map((bill) => {
                          const active = isActiveAnalysis(
                            bill.analysis.status,
                            watchIds.includes(bill.id),
                            bill.analysis.updated_at
                          );
                          const isEnqueueing = processingBillId === bill.id;

                          return (
                            <tr key={bill.id} className="border-t">
                              <td className="px-4 py-3">
                                <div className="font-medium">{bill.name}</div>
                                <div className="text-xs text-muted-foreground">{bill.document_type}</div>
                                {bill.analysis.error_message ? (
                                  <div className="mt-1 text-xs text-red-600">{bill.analysis.error_message}</div>
                                ) : null}
                              </td>
                              <td className="px-4 py-3">
                                <AnalysisStatusCell
                                  analysis={bill.analysis}
                                  billId={bill.id}
                                  watchIds={watchIds}
                                  nowMs={nowMs}
                                />
                              </td>
                              <td className="px-4 py-3 text-muted-foreground">
                                {formatTimestamp(bill.analysis.updated_at)}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex flex-wrap items-center gap-2">
                                  <Button
                                    type="button"
                                    size="sm"
                                    disabled={active || isEnqueueing}
                                    onClick={() => onPrecompute(bill.id)}
                                  >
                                    {isEnqueueing ? (
                                      <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Enqueueing
                                      </>
                                    ) : active ? (
                                      "Running"
                                    ) : (
                                      "Precompute"
                                    )}
                                  </Button>
                                  <Button type="button" size="sm" variant="outline" asChild>
                                    <Link href={billDetailHref(bill.id)}>Results</Link>
                                  </Button>
                                  <Button type="button" size="sm" variant="outline" asChild>
                                    <a href={bill.madmin_url} target="_blank" rel="noreferrer">
                                      <ExternalLink className="mr-2 h-4 w-4" />
                                      Madmin
                                    </a>
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <PaginatedTableControls
                    variant="compact"
                    currentPage={pagination.current_page}
                    totalPages={pagination.total_pages}
                    perPage={pagination.per_page}
                    totalCount={pagination.total_count}
                    perPageSelectId="legislation-monitor-per-page"
                    onPageChange={onPageChange}
                    onPerPageChange={onPerPageChange}
                  />
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
